import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../Context/AuthContext';
import { toast } from 'sonner';

const AuditOrder = () => {
  const navigate = useNavigate();
  const { sellerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getToken } = useAuth();

  const [searchTerm] = useState(searchParams.get('search') || '');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, lastPage: 1, perPage: 20, total: 0 });

  function timeAgo(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return '1 day ago';
    return `${diff} days ago`;
  }

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        // setError(null);
        const token = getToken();
        if (!token) {
          // setError('No authentication token found');
          setLoading(false);
          return;
        }

        const params = new URLSearchParams();
        const page = parseInt(searchParams.get('page') || '1', 10);
        if (page > 1) params.append('page', String(page));
        const search = searchParams.get('search');
        if (search) params.append('search', search);

        const url = `https://apisellhub.com/api//admin/sellers-log/${sellerId}?${params.toString()}`;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.data?.status) {
          setLogs(response.data.data || []);
          setPagination({
            currentPage: response.data.page_count.current_page,
            lastPage: response.data.page_count.last_page,
            perPage: response.data.page_count.per_page,
            total: response.data.page_count.total,
          });
        } else {
          // setError('Failed to load audit logs');
        }
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/center/login", { replace: true });
          sessionStorage.removeItem("Market-token");
          toast.error('Token Expired. Please login again.');
          // setError('Authentication failed. Please login again.');
        } else {
          // setError('Failed to load dashboard data');
        }
        console.error('Audit logs API error:', err);
        // setError('Failed to load audit logs');
      } finally {
        setLoading(false);
      }
    };

    if (sellerId) {
      fetchLogs();
    }
  }, [sellerId, searchParams, getToken,navigate]);

  const filteredLogs = useMemo(() => {
    const localSearch = searchTerm.trim().toLowerCase();
    if (!localSearch) return logs;
    return logs.filter((log) => {
      const message = JSON.stringify(log || {}).toLowerCase();
      return message.includes(localSearch);
    });
  }, [logs, searchTerm]);

  // const handleSearchApply = () => {
  //   const next = new URLSearchParams(searchParams);
  //   if (searchTerm.trim()) next.set('search', searchTerm.trim());
  //   else next.delete('search');
  //   next.set('page', '1');
  //   setSearchParams(next);
  // };

  const handlePageChange = (page) => {
    const pageNum = Math.max(1, Math.min(page, pagination.lastPage));
    const next = new URLSearchParams(searchParams);
    next.set('page', String(pageNum));
    setSearchParams(next);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/center/seller')}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Seller</span>
          </button>
        </div>
      </div>
      <div>
        <h2 className="text-2xl font-bold text-white">Seller Audit Log</h2>
      </div>

     

      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">LOG NAME</th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">EVENT</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">DESCRIPTION</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Created At</th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {loading && (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-gray-400">Loading...</td>
                </tr>
              )}
              {!loading && filteredLogs.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-gray-400">No logs found</td>
                </tr>
              )}
              {!loading && filteredLogs.map((log) => (
                <tr key={log.id || JSON.stringify(log)} className="hover:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">{log.id || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">{log.log_name || log.log_name || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">{log.event || log.event || '-'}</div>
                  </td>
                 
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-300 break-words">
                      {log.description || log.description || (
                        <pre className="whitespace-pre-wrap break-words text-gray-400 text-xs">{JSON.stringify(log, null, 2)}</pre>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">{timeAgo(log.created_at)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.total > 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Showing {(pagination.currentPage - 1) * pagination.perPage + 1} to {Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage <= 1} className="px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50">
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-white bg-yellow-600 rounded">{pagination.currentPage}</span>
              <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage >= pagination.lastPage} className="px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50">
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditOrder;

